import React, { useState } from 'react';
import axios from 'axios';

const EmailVerificationHandler = () => {
  const [emailToVerify, setEmailToVerify] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const handleResendEmail = async () => {
    if (!emailToVerify) {
      setMessage('Please enter an email address.');
      setMessageType('error');
      return;
    }

    setIsResending(true);
    setMessage('');

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/verification/resend-verification-email`,
        { email: emailToVerify }
      );
      setMessage('Verification email sent! Please check your inbox.');
      setMessageType('success');
    } catch (error) {
      console.error('Error in resending email:', error);
      const errorMessage =
        error.response?.data?.message ||
        'An error occurred while trying to resend the verification email.';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="email-verify-page">
      <div className="email-verify-card">
        {/* Icon */}
        <div className="email-verify-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M22 6L12 13L2 6" />
          </svg>
        </div>

        <h2 className="email-verify-title">Verify Your Email</h2>

        <p className="email-verify-text">
          Please verify your email address. Check your inbox for a verification email.
          If you don't see it, check your spam folder or resend it below.
        </p>

        {/* Status Message */}
        {message && (
          <div className={`email-verify-message ${messageType}`}>
            {message}
          </div>
        )}

        {/* Resend Form */}
        <div className="email-verify-form">
          <input
            type="email"
            className="email-verify-input"
            placeholder="Enter your email"
            value={emailToVerify}
            onChange={(e) => setEmailToVerify(e.target.value)}
          />
          <button
            className="email-verify-btn"
            onClick={handleResendEmail}
            disabled={isResending}
          >
            {isResending ? 'Sending...' : 'Resend Verification Email'}
          </button>
        </div>

        <p className="email-verify-footer">
          Already verified? <a href="/" className="email-verify-link">Go to Home</a>
        </p>
      </div>

      <style>{`
        .email-verify-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px 40px;
          background: linear-gradient(180deg, #f9fafb 0%, #fff 100%);
        }

        .email-verify-card {
          width: 100%;
          max-width: 460px;
          text-align: center;
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          padding: 48px 36px;
        }

        .email-verify-icon {
          color: #ff8264;
          margin-bottom: 20px;
        }

        .email-verify-title {
          font-size: 24px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 12px 0;
        }

        .email-verify-text {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.6;
          margin: 0 0 24px 0;
        }

        .email-verify-message {
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .email-verify-message.success {
          background: #f0fdf4;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .email-verify-message.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .email-verify-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .email-verify-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 10px;
          font-size: 14px;
          color: #111827;
          box-sizing: border-box;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .email-verify-input:focus {
          outline: none;
          border-color: #ff8264;
          box-shadow: 0 0 0 3px rgba(255, 130, 100, 0.15);
        }

        .email-verify-btn {
          width: 100%;
          padding: 12px 24px;
          background: #ff8264;
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s, transform 0.1s;
        }

        .email-verify-btn:hover:not(:disabled) {
          background: #e5745a;
          transform: translateY(-1px);
        }

        .email-verify-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .email-verify-footer {
          margin-top: 24px;
          font-size: 13px;
          color: #9ca3af;
        }

        .email-verify-link {
          color: #ff8264;
          text-decoration: none;
          font-weight: 500;
        }

        .email-verify-link:hover {
          text-decoration: underline;
        }

        @media (max-width: 480px) {
          .email-verify-card {
            padding: 32px 24px;
          }
        }
      `}</style>
    </div>
  );
};

export default EmailVerificationHandler;
